import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search } from 'lucide-react';
import { Button, Badge, Table, Modal, Input, Select } from '@/components/ui';
import { campaignService } from '@/services/campaignService';
import type {
  Campaign,
  CampaignStatus,
  CreateCampaignRequest,
  UpdateCampaignRequest,
} from '@/types/campaign';
import styles from './CampaignsPage.module.css';

type BadgeVariant = 'neutral' | 'success' | 'warning' | 'info';

const STATUS_LABELS: Record<CampaignStatus, string> = {
  DRAFT: 'Rascunho',
  ACTIVE: 'Ativa',
  PAUSED: 'Pausada',
  FINISHED: 'Finalizada',
};

const STATUS_BADGE_VARIANT: Record<CampaignStatus, BadgeVariant> = {
  DRAFT: 'neutral',
  ACTIVE: 'success',
  PAUSED: 'warning',
  FINISHED: 'info',
};

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'Todos os status' },
  { value: 'DRAFT', label: 'Rascunho' },
  { value: 'ACTIVE', label: 'Ativa' },
  { value: 'PAUSED', label: 'Pausada' },
  { value: 'FINISHED', label: 'Finalizada' },
];

/**
 * Admin page for listing and managing campaigns.
 *
 * Features:
 * - Filterable campaign list by status and search term
 * - Create new campaign via modal
 * - Edit existing campaign via modal
 * - Status transitions: Activate, Pause, Resume, Finish
 * - Click row to navigate to campaign detail page
 */
export function CampaignsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<CampaignStatus | ''>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formStartDate, setFormStartDate] = useState('');
  const [formEndDate, setFormEndDate] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const queryParams = {
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(searchQuery.trim() ? { search: searchQuery.trim() } : {}),
  };

  const {
    data: campaigns = [],
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ['campaigns', queryParams],
    queryFn: () => campaignService.getCampaigns(
      Object.keys(queryParams).length > 0 ? queryParams : undefined,
    ),
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateCampaignRequest) =>
      campaignService.createCampaign(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      closeCreateModal();
    },
    onError: () => {
      setFormError('Erro ao criar campanha. Tente novamente.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCampaignRequest }) =>
      campaignService.updateCampaign(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      closeEditModal();
    },
    onError: () => {
      setFormError('Erro ao atualizar campanha. Tente novamente.');
    },
  });

  const activateMutation = useMutation({
    mutationFn: (id: string) => campaignService.activateCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });

  const pauseMutation = useMutation({
    mutationFn: (id: string) => campaignService.pauseCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });

  const resumeMutation = useMutation({
    mutationFn: (id: string) => campaignService.resumeCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });

  const finishMutation = useMutation({
    mutationFn: (id: string) => campaignService.finishCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });

  const isStatusChangePending =
    activateMutation.isPending ||
    pauseMutation.isPending ||
    resumeMutation.isPending ||
    finishMutation.isPending;

  const resetForm = useCallback(() => {
    setFormName('');
    setFormDescription('');
    setFormStartDate('');
    setFormEndDate('');
    setFormError(null);
  }, []);

  const openCreateModal = useCallback(() => {
    resetForm();
    setIsCreateModalOpen(true);
  }, [resetForm]);

  const closeCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
    setFormError(null);
  }, []);

  const openEditModal = useCallback((campaign: Campaign) => {
    setFormName(campaign.name);
    setFormDescription(campaign.description ?? '');
    setFormStartDate(campaign.startDate);
    setFormEndDate(campaign.endDate);
    setFormError(null);
    setEditingCampaign(campaign);
  }, []);

  const closeEditModal = useCallback(() => {
    setEditingCampaign(null);
    setFormError(null);
  }, []);

  const buildPayload = useCallback((): CreateCampaignRequest | null => {
    if (!formName.trim()) {
      setFormError('Nome é obrigatório.');
      return null;
    }
    if (!formStartDate) {
      setFormError('Data de início é obrigatória.');
      return null;
    }
    if (!formEndDate) {
      setFormError('Data de fim é obrigatória.');
      return null;
    }
    if (formStartDate > formEndDate) {
      setFormError('Data de início deve ser anterior à data de fim.');
      return null;
    }
    return {
      name: formName.trim(),
      description: formDescription.trim() || null,
      startDate: formStartDate,
      endDate: formEndDate,
    };
  }, [formName, formDescription, formStartDate, formEndDate]);

  const handleCreate = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      const payload = buildPayload();
      if (payload) {
        createMutation.mutate(payload);
      }
    },
    [buildPayload, createMutation],
  );

  const handleUpdate = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      if (!editingCampaign) return;
      const payload = buildPayload();
      if (payload) {
        updateMutation.mutate({ id: editingCampaign.id, data: payload });
      }
    },
    [buildPayload, editingCampaign, updateMutation],
  );

  const handleStatusAction = useCallback(
    (event: React.MouseEvent, campaign: Campaign) => {
      event.stopPropagation();
      switch (campaign.status) {
        case 'DRAFT':
          activateMutation.mutate(campaign.id);
          break;
        case 'ACTIVE':
          pauseMutation.mutate(campaign.id);
          break;
        case 'PAUSED':
          resumeMutation.mutate(campaign.id);
          break;
      }
    },
    [activateMutation, pauseMutation, resumeMutation],
  );

  const handleFinish = useCallback(
    (event: React.MouseEvent, campaign: Campaign) => {
      event.stopPropagation();
      finishMutation.mutate(campaign.id);
    },
    [finishMutation],
  );

  const handleEditClick = useCallback(
    (event: React.MouseEvent, campaign: Campaign) => {
      event.stopPropagation();
      openEditModal(campaign);
    },
    [openEditModal],
  );

  const handleRowClick = useCallback(
    (campaign: Campaign) => {
      navigate(`/admin/campaigns/${campaign.id}`);
    },
    [navigate],
  );

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('pt-BR');
  };

  const getStatusActionLabel = (status: CampaignStatus): string | null => {
    switch (status) {
      case 'DRAFT':
        return 'Ativar';
      case 'ACTIVE':
        return 'Pausar';
      case 'PAUSED':
        return 'Retomar';
      default:
        return null;
    }
  };

  const canFinish = (status: CampaignStatus): boolean => {
    return status === 'ACTIVE' || status === 'PAUSED';
  };

  const columns = [
    {
      key: 'name',
      header: 'Nome',
      render: (campaign: Campaign) => (
        <span className={styles.campaignName}>{campaign.name}</span>
      ),
    },
    {
      key: 'period',
      header: 'Período',
      width: '200px',
      render: (campaign: Campaign) => (
        <span className={styles.campaignPeriod}>
          {formatDate(campaign.startDate)} - {formatDate(campaign.endDate)}
        </span>
      ),
    },
    {
      key: 'packages',
      header: 'Pacotes',
      width: '100px',
      render: (campaign: Campaign) => (
        <span>{campaign.packages.length}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '120px',
      render: (campaign: Campaign) => (
        <Badge variant={STATUS_BADGE_VARIANT[campaign.status]}>
          {STATUS_LABELS[campaign.status]}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: 'Ações',
      width: '260px',
      render: (campaign: Campaign) => {
        const statusAction = getStatusActionLabel(campaign.status);

        return (
          <div className={styles.actions}>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => handleEditClick(e, campaign)}
              disabled={campaign.status === 'FINISHED'}
            >
              Editar
            </Button>
            {statusAction && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => handleStatusAction(e, campaign)}
                disabled={isStatusChangePending}
              >
                {statusAction}
              </Button>
            )}
            {canFinish(campaign.status) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => handleFinish(e, campaign)}
                disabled={isStatusChangePending}
              >
                Finalizar
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  const formFields = (
    <>
      <Input
        label="Nome"
        value={formName}
        onChange={(e) => setFormName(e.target.value)}
        placeholder="Nome da campanha"
        required
        fullWidth
      />
      <div className={styles.textareaWrapper}>
        <label htmlFor="campaign-description" className={styles.textareaLabel}>
          Descrição (opcional)
        </label>
        <textarea
          id="campaign-description"
          className={styles.textarea}
          value={formDescription}
          onChange={(e) => setFormDescription(e.target.value)}
          rows={3}
          placeholder="Descreva a campanha..."
        />
      </div>
      <div className={styles.dateRow}>
        <Input
          label="Data de início"
          type="date"
          value={formStartDate}
          onChange={(e) => setFormStartDate(e.target.value)}
          required
          fullWidth
        />
        <Input
          label="Data de fim"
          type="date"
          value={formEndDate}
          onChange={(e) => setFormEndDate(e.target.value)}
          required
          fullWidth
        />
      </div>
      {formError && (
        <p className={styles.formError} role="alert">
          {formError}
        </p>
      )}
    </>
  );

  if (queryError) {
    return (
      <div className={styles.page}>
        <div className={styles.errorState} role="alert">
          <p>Erro ao carregar campanhas. Tente novamente mais tarde.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Campanhas</h1>
          <p className={styles.subtitle}>
            Gerencie campanhas e pacotes da clínica.
          </p>
        </div>
        <Button leftIcon={<Plus size={18} />} onClick={openCreateModal}>
          Nova Campanha
        </Button>
      </header>

      <div className={styles.filters}>
        <div className={styles.searchWrapper}>
          <Input
            label="Buscar"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar por nome..."
            leftIcon={<Search size={16} />}
            fullWidth
          />
        </div>
        <div className={styles.statusFilter}>
          <Select
            label="Filtrar por status"
            options={STATUS_FILTER_OPTIONS}
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as CampaignStatus | '')
            }
          />
        </div>
      </div>

      {isLoading ? (
        <div className={styles.loadingState} role="status" aria-busy="true">
          <p>Carregando campanhas...</p>
        </div>
      ) : (
        <Table
          columns={columns}
          data={campaigns}
          keyExtractor={(campaign) => campaign.id}
          onRowClick={handleRowClick}
          emptyMessage="Nenhuma campanha encontrada."
        />
      )}

      {/* Create modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={closeCreateModal}
        title="Nova Campanha"
        size="md"
      >
        <form onSubmit={handleCreate} className={styles.form} noValidate>
          {formFields}
          <div className={styles.formActions}>
            <Button variant="ghost" type="button" onClick={closeCreateModal}>
              Cancelar
            </Button>
            <Button type="submit" isLoading={createMutation.isPending}>
              Criar
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit modal */}
      <Modal
        isOpen={editingCampaign !== null}
        onClose={closeEditModal}
        title="Editar Campanha"
        size="md"
      >
        <form onSubmit={handleUpdate} className={styles.form} noValidate>
          {formFields}
          <div className={styles.formActions}>
            <Button variant="ghost" type="button" onClick={closeEditModal}>
              Cancelar
            </Button>
            <Button type="submit" isLoading={updateMutation.isPending}>
              Salvar
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
